import { DISPLAY_TIME_ZONE } from '@lions/contracts';

import { type Dayjs, dayjs } from './dayjs';

/**
 * Dates in the admin panel (02-technical-architecture.md § Time): instants are stored and sent in
 * UTC; calendar days and everything shown to admins use Africa/Johannesburg (SAST, UTC+2).
 * A "day" is a `YYYY-MM-DD` string, the shape of the API's `from` and `to` filters.
 */

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const dayLabelFormat = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

const dateFormat = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  timeZone: DISPLAY_TIME_ZONE,
});

const dateTimeFormat = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: DISPLAY_TIME_ZONE,
});

const isoDayFormat = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  timeZone: DISPLAY_TIME_ZONE,
});

export function isDay(value: string): boolean {
  return DAY_PATTERN.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));
}

/** `2026-04-07` → `7 Apr 2026`. A day is a calendar label, so it is formatted without a zone shift. */
export function formatDay(day: string): string {
  return dayLabelFormat.format(new Date(`${day}T00:00:00Z`));
}

/** UTC instant → `06 Jul 2026` in SAST (the legacy table date format). */
export function formatDate(instant: string | Date): string {
  return dateFormat.format(new Date(instant));
}

/** UTC instant → `06 Jul 2026, 14:30` in SAST. */
export function formatDateTime(instant: string | Date): string {
  return dateTimeFormat.format(new Date(instant));
}

/** The SAST calendar day an instant falls on: `2026-10-12T22:30:00Z` → `2026-10-13`. */
export function sastDayOf(instant: string | Date): string {
  return isoDayFormat.format(new Date(instant));
}

/** Today's calendar day in SAST. */
export function todaySast(now: Date = new Date()): string {
  return sastDayOf(now);
}

/** The day `days` after (or before, when negative) a day: `addDays('2026-10-01', -1)` → `2026-09-30`. */
export function addDays(day: string, days: number): string {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** The first day of the month `months` before the month of `day`: `('2026-10-19', 5)` → `2026-05-01`. */
export function monthStartBefore(day: string, months: number): string {
  const date = new Date(`${day.slice(0, 7)}-01T00:00:00Z`);
  date.setUTCMonth(date.getUTCMonth() - months);
  return date.toISOString().slice(0, 10);
}

/** The season has no defined start (open question): the last six calendar months. */
export const SEASON_MONTHS = 6;

/** The first day of the season that contains `day` (six calendar months including its own). */
export function seasonStart(day: string): string {
  return monthStartBefore(day, SEASON_MONTHS - 1);
}

/** A day as a picker value: midnight of that day in SAST. */
export function dayToPickerValue(day: string | null): Dayjs | null {
  return day ? dayjs.tz(day, DISPLAY_TIME_ZONE) : null;
}

/** A picker value back to its SAST calendar day, or null when empty or invalid. */
export function pickerValueToDay(value: Dayjs | null): string | null {
  if (!value?.isValid()) return null;
  return value.tz(DISPLAY_TIME_ZONE).format('YYYY-MM-DD');
}

const localPartsFormat = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: DISPLAY_TIME_ZONE,
});

const partsOf = (format: Intl.DateTimeFormat, date: Date) =>
  Object.fromEntries(format.formatToParts(date).map((part) => [part.type, part.value]));

const longDayFormat = new Intl.DateTimeFormat('en-GB', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

const shortDayFormat = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});

const timeFormat = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: DISPLAY_TIME_ZONE,
});

/** `2026-10-19` → `Monday, 19 October 2026` (the dashboard greeting). */
export function formatLongDay(day: string): string {
  const parts = partsOf(longDayFormat, new Date(`${day}T00:00:00Z`));
  return `${parts.weekday}, ${parts.day} ${parts.month} ${parts.year}`;
}

/** `2026-10-31` → `Sat 31 Oct` (chart tooltips, match dates). */
export function formatShortDay(day: string): string {
  const parts = partsOf(shortDayFormat, new Date(`${day}T00:00:00Z`));
  return `${parts.weekday} ${parts.day} ${parts.month}`;
}

/** UTC instant → `14:00` in SAST. */
export function formatTime(instant: string | Date): string {
  return timeFormat.format(new Date(instant));
}

/** Whole days from one day to another (negative when `to` is earlier). */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** The SAST hour (0 to 23) of an instant. */
export function sastHour(now: Date = new Date()): number {
  return Number(partsOf(timeFormat, now).hour);
}

/**
 * How long ago an instant was, for activity lists: `Just now`, `12 min ago`, `5h ago`, `Yesterday`
 * (the SAST calendar day before today), `3 days ago`, then the date.
 */
export function formatTimeAgo(instant: string | Date, now: Date = new Date()): string {
  const minutes = Math.floor((now.getTime() - new Date(instant).getTime()) / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const days = daysBetween(sastDayOf(instant), todaySast(now));
  if (days === 0 || minutes < 6 * 60) return `${Math.floor(minutes / 60)}h ago`;
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return formatDate(instant);
}

/** UTC instant → SAST wall-clock `YYYY-MM-DDTHH:mm` (a `datetime-local` input value). */
export function sastLocalOf(instant: string | Date): string {
  const parts = Object.fromEntries(
    localPartsFormat.formatToParts(new Date(instant)).map((part) => [part.type, part.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

/** SAST wall-clock `YYYY-MM-DDTHH:mm` → ISO instant with the SAST offset (UTC+2, no DST). */
export function sastLocalToIso(local: string): string {
  return `${local}:00+02:00`;
}
